const { formatDateForApi } = require('@/lib/Dates/dateFunctions');
const { removeNullValues } = require('@/functions/functions');
const { toNullableBoolInt } = require('@/functions/transform');

/**
 * @typedef {Object} ReservationStructure
 * @property {number|null} customerAccountID
 * @property {string} date
 * @property {number|null} deviceID
 * @property {number|null} existingOrderID
 * @property {Object} lines
 * @property {number|null} membershipID
 * @property {string} thirdPartyRef
 */

/**
 * @constructor
 *
 * @param {CartLineSplitter} cartLineSplitter
 * @param {CurrentDevice} currentDevice
 * @param {UserService} userService
 */
const CartToReservationStructureMapper = function (
    cartLineSplitter,
    currentDevice,
    userService
) {
    /**
     * @type {CartLineSplitter}
     */
    this.cartLineSplitter = cartLineSplitter;

    /**
     * @type {CurrentDevice}
     */
    this.currentDevice = currentDevice;

    /**
     * @type {UserService}
     */
    this.userService = userService;
};

CartToReservationStructureMapper.prototype = {
    /**
     * Convert a cart to the structure required for the reservations API endpoint.
     *
     * @param {Cart} cart
     * @param {boolean} [splitDiscounts=false]
     *
     * @returns {ReservationStructure}
     */
    map(cart, splitDiscounts = true) {
        const createdDate = new Date();
        const createdDateString = formatDateForApi(createdDate);

        return {
            customerAccountID: cart.customer.account ? cart.customer.account.ID : null,
            date: createdDateString,
            deviceID: this.currentDevice.isSet() ? this.currentDevice.getDeviceId() : null,
            existingOrderID: cart.originalOrder ? cart.originalOrder.ID : null,
            lines: this.mapLines(cart, splitDiscounts),
            // This membershipID is here because this 'reservation structure' is also sent to the API to calculate
            // offers, not just to make a reservation. So it is needed to unlock member-only discounts.
            membershipID: cart.customer.membership ? cart.customer.membership.ID : null,
            // Sending thirdPartyRef allows us to easily link reservation requests to order requests in Cloudwatch.
            thirdPartyRef: cart.thirdPartyRef,
        };
    },

    /**
     * @param {Cart} cart
     * @param {boolean} [splitDiscounts=true]
     *
     * @return {object}
     */
    mapLines(cart, splitDiscounts = true) {
        let lines = cart.exportItems();

        if (cart.cartProductStockLevels?.hasStockLevels()) {
            lines = this.processStockLevels(cart, lines);
        }

        if (splitDiscounts) {
            lines = this.cartLineSplitter.splitDiscounts(lines);
        }

        return this.convertCartLinesToReservationLines(lines, cart, !splitDiscounts);
    },

    /**
     * Returns an object containing all the cart lines, keyed by the line's lineNumber.
     *
     * @param {CartLine[]} cartLines
     * @param {Cart} cart
     * @param {boolean} [useLineNumber=false] Whether or not to use the line numbers of the existing cart lines as the
     * keys in the returned object?
     *
     * @return {Object<Object>}
     */
    convertCartLinesToReservationLines(cartLines, cart, useLineNumber = false) {
        let reservationLines = {};
        for (let i = 0; i < cartLines.length; i++) {
            let key = useLineNumber ? cartLines[i].lineNumber : i;
            reservationLines[key] = this.convertCartLineToReservationLine(cartLines[i], cart);
        }

        return reservationLines;
    },

    /**
     * @param {Cart} cart
     * @param {CartLine[]} cartLines
     *
     * @returns {CartLine[]}
     */
    processStockLevels(cart, cartLines) {
        cartLines.forEach((cartLine) => {
            let stockLevel = cart.cartProductStockLevels.getStockLevel(
                cartLine.branch.ID,
                cartLine.item.ID
            );
            if (stockLevel) {
                cartLine.disableStockValidation = stockLevel.disableStockValidation;
            }
        });
        return cartLines;
    },

    /**
     * @param {CartLine} cartLine
     * @param {Cart} cart
     *
     * @return {object}
     */
    convertCartLineToReservationLine(cartLine, cart) {
        let discount = cartLine.getFirstActiveDiscount();

        let line = {
            baseTotal: cartLine.getBaseTotal(),
            branchID: cartLine.branch.ID,
            discountAmount: cartLine.discountAmount,
            discountedBaseTotal: cartLine.getDiscountedBaseTotal(),
            discountPercentage: cartLine.discountPercentage,
            donation: cartLine.getDonation(),
            extraData: this.getExtraDataForCartLineReservation(cartLine),
            giftaid: cartLine.giftAid ? 1 : 0,
            itemID: cartLine.item.ID,
            itemInstanceIDs: cartLine.getItemInstances().map((ii) => ii.ID),
            itemType: cartLine.getItemType(),
            lineNumber: cartLine.lineNumber,
            lineTotal: cartLine.getLineTotal(),
            membershipSubscriptionApportionedAmounts: cartLine.membershipSubscriptionApportionedAmounts,
            offerID: discount ? discount.offerID : null,
            offersCodesID: discount ? discount.offersCodesID : null,
            price: cartLine.getPrice(),
            bookingFee: cartLine.bookingFee,
            qty: cartLine.getQuantity(),
            returnReasonID: cartLine.returnReason.reasonID,
            returnToStock: toNullableBoolInt(cartLine.returnReason.returnToStock),
            sessionID: cartLine.session ? cartLine.session.ID : null,
            signature: cartLine.getID(),
            soldGiftVoucherData: cartLine.soldGiftVoucherData ? cartLine.soldGiftVoucherData.toServerData() : null,
            thirdPartyRef: cartLine.thirdPartyRef,
        };

        if (cartLine.groupBookingReference) {
            cart.bookingGroups[cartLine.thirdPartyRef] = cartLine.groupBookingReference;
        }

        // If this is an order edit then some or all of this line may already have been purchased. We need to send this
        // quantity to the API so it can be taken into account for the reservation and stock check.
        // Ignore qtyBeforeAdjustment < 0 for now: see PRO-1406 Handle editing an order with a refund (-ve qty) line.
        if (cart && cart.originalOrder && cartLine.qtyBeforeAdjustment >= 0) {
            line.qtyBeforeAdjustment = cartLine.qtyBeforeAdjustment;
        }

        const isAdjusted = cartLine.isAdjusted();
        if (isAdjusted && cartLine.ordersItemsID) {
            // The line has been modified.
            line.adjustedLineID = cartLine.ordersItemsID;
        } else if (cartLine.ordersItemsID) {
            // The line came from an original line in the order and is unmodified.
            line.ordersItemsID = cartLine.ordersItemsID;
        }

        // The operator has manually disabled the stock level check for this line.
        if (cartLine.disableStockValidation) {
            line.disableStockValidation = cartLine.disableStockValidation;
        }

        removeNullValues(line);

        return line;
    },

    /**
     * @param {CartLine} cartLine
     *
     * @return {object}
     */
    getExtraDataForCartLineReservation(cartLine) {
        let extraData = {};

        let discountNarrative = cartLine.getVariableDiscountNarrative();
        if (discountNarrative) {
            extraData.discountNarrative = discountNarrative;
        }

        if (cartLine.parentThirdPartyRef) {
            extraData.parentThirdPartyRef = cartLine.parentThirdPartyRef;
        }

        if (cartLine.optionSet) {
            extraData.optionSetID = cartLine.optionSet.ID;
        }

        return extraData;
    },

    /**
     * Remove lines with quantity = 0 because this is refund from an order adjustment and we can't send a empty
     * line to the reservation endpoint.
     *
     * @param reservationStructure
     *
     * @return {object}
     */
    removeZeroQtyLines(reservationStructure) {
        Object.keys(reservationStructure.lines).forEach((key) => {
            let line = reservationStructure.lines[key];
            if (line.qty === 0) {
                delete reservationStructure.lines[key];
            }
        });

        return reservationStructure;
    },
};

module.exports = CartToReservationStructureMapper;
